import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach bearer token as a safety net for envs where 3rd-party cookies are blocked
api.interceptors.request.use((config) => {
  const t = localStorage.getItem("zj_access_token");
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Response interceptor — ensure error.response.data.detail is ALWAYS a
// human-readable string so any legacy `toast.error(e?.response?.data?.detail)`
// never displays "[object Object]".
api.interceptors.response.use(
  (r) => r,
  (error) => {
    try {
      const data = error?.response?.data;
      if (data && typeof data === "object") {
        if (data.detail !== undefined && typeof data.detail !== "string") {
          data.detail = formatApiErrorDetail(data.detail);
        }
        if (data.message !== undefined && typeof data.message !== "string") {
          data.message = formatApiErrorDetail(data.message);
        }
      }
    } catch { /* never let the interceptor throw */ }
    return Promise.reject(error);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => {
        if (!e) return "";
        if (typeof e === "string") return e;
        if (typeof e.msg === "string") {
          const loc = Array.isArray(e.loc) ? e.loc.filter((p) => p !== "body").join(".") : "";
          return loc ? `${loc}: ${e.msg}` : e.msg;
        }
        try { return JSON.stringify(e); } catch { return String(e); }
      })
      .filter(Boolean)
      .join(" · ");
  if (detail && typeof detail === "object") {
    // Zaroori Jini structured document / marketplace validation errors —
    // never leak the raw JSON to the toast; turn it into a readable line.
    // Shape: { code, doc_type?, missing: string[], hint: string }
    if (Array.isArray(detail.missing) && (detail.code || detail.hint)) {
      const label = ({
        COMPANY_MASTER_INCOMPLETE:    "Company Master is incomplete",
        DOCUMENT_VALIDATION_FAILED:   "Cannot print — required fields are missing",
        MARKETPLACE_PROFILE_INCOMPLETE: "Marketplace validation failed",
      }[detail.code] || detail.code || "Validation failed");
      const doc = detail.doc_type ? ` (${detail.doc_type})` : "";
      const items = detail.missing.slice(0, 4).join(", ");
      const more = detail.missing.length > 4 ? ` (+${detail.missing.length - 4} more)` : "";
      const hint = detail.hint ? ` — ${detail.hint}` : "";
      return `${label}${doc}: ${items}${more}${hint}`;
    }
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.error === "string") return detail.error;
    if (typeof detail.detail === "string") return detail.detail;
    try { return JSON.stringify(detail); } catch { return String(detail); }
  }
  return String(detail);
}

// Safe error → string extractor for any thrown error (axios, fetch, generic).
// NEVER returns "[object Object]".
export function extractErrorMessage(err, fallback = "Something went wrong. Please try again.") {
  if (err == null) return fallback;
  if (typeof err === "string") return err;
  // Axios error shape
  const axiosDetail = err?.response?.data?.detail ?? err?.response?.data?.message ?? err?.response?.data;
  if (axiosDetail != null && axiosDetail !== "") {
    const s = formatApiErrorDetail(axiosDetail);
    if (s && s !== "[object Object]") return s;
  }
  // Fetch-wrapped error whose message is a stringified object
  if (err instanceof Error && typeof err.message === "string") {
    const m = err.message;
    if (m && m !== "[object Object]") return m;
    // Try to unwrap `err.cause` if present (structured fetch error)
    if (err.cause) {
      const c = extractErrorMessage(err.cause, "");
      if (c) return c;
    }
  }
  if (typeof err === "object") {
    if (typeof err.message === "string" && err.message && err.message !== "[object Object]") return err.message;
    if (typeof err.detail === "string" && err.detail) return err.detail;
    try {
      const s = JSON.stringify(err);
      if (s && s !== "{}") return s;
    } catch { /* noop */ }
  }
  return fallback;
}

import { toast as sonnerToast } from "sonner";
export function toastError(err, fallback) {
  sonnerToast.error(extractErrorMessage(err, fallback));
}
